/**
 * Página para admins de asociación para ver y gestionar solicitudes
 * de aprobación de nuevos clubes.
 */

import { useEffect, useState } from "react";
import { useAuthStore } from "@/stores/authStore";
import {
  watchAssociationPendingRequests,
  approveClubRequest,
  rejectClubRequest,
  type ClubApprovalRequest,
} from "@/lib/services/clubApprovalRequests";
import { CLUB_TYPE_LABELS } from "@/types";
import type { ClubType } from "@/types";
import { sortSections } from "@/lib/constants/sections";
import { formatDate } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import {
  Shield,
  Users,
  Building2,
  Mail,
  Phone,
  Calendar,
  MapPin,
  CheckCircle2,
  XCircle,
  Loader2,
  Inbox,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui";
import { Can } from "@/components/auth/Can";
import { clubService } from "@/lib/services/organization";

export default function ClubApprovalRequestsPage() {
  const { profile } = useAuthStore();
  const associationId = profile?.scopeId;

  const [requests, setRequests] = useState<ClubApprovalRequest[]>([]);
  // clubIds de solicitudes cuyo nombre coincide con un club ACTIVO existente
  const [dupWarnings, setDupWarnings] = useState<Map<string, string>>(
    new Map(),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => {
    if (!associationId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const unsubscribe = watchAssociationPendingRequests(
      associationId,
      (data) => {
        setRequests(data);
        setLoading(false);
      },
      (err) => {
        console.error("Error loading club approval requests:", err);
        setError("Error al cargar las solicitudes");
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [associationId]);

  // Detectar posibles duplicados: solicitudes cuyo nombre coincide con un club
  // activo existente, O cuya IGLESIA ya tiene club (una iglesia = un club, con
  // varias secciones). El aspirante debería unirse, no crear otro club.
  useEffect(() => {
    let active = true;
    (async () => {
      const map = new Map<string, string>();
      for (const req of requests) {
        try {
          const similar = await clubService.findPossibleDuplicates({
            name: req.clubName,
            churchId: req.clubData?.churchId,
            churchName: req.clubData?.churchName,
          });
          const others = similar.filter((c) => c.id !== req.clubId);
          if (others.length > 0) {
            map.set(req.id, others.map((c) => c.name).join(", "));
          }
        } catch {
          /* sin bloqueo si falla la consulta */
        }
      }
      if (active) setDupWarnings(map);
    })();
    return () => {
      active = false;
    };
  }, [requests]);

  const handleApprove = async (request: ClubApprovalRequest) => {
    if (!profile?.id) return;

    setProcessingId(request.id);
    setError(null);

    try {
      await approveClubRequest(request.id, profile.id);
    } catch (err) {
      console.error("Error approving club request:", err);
      setError(
        "Error al aprobar la solicitud. " +
          (err instanceof Error ? err.message : ""),
      );
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (request: ClubApprovalRequest) => {
    if (!profile?.id || !rejectReason.trim()) return;

    setProcessingId(request.id);
    setError(null);

    try {
      await rejectClubRequest(request.id, profile.id, rejectReason.trim());
      setRejectingId(null);
      setRejectReason("");
    } catch (err) {
      console.error("Error rejecting club request:", err);
      setError(
        "Error al rechazar la solicitud. " +
          (err instanceof Error ? err.message : ""),
      );
    } finally {
      setProcessingId(null);
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
    // Cerrar reject si estaba abierto
    if (rejectingId === id) {
      setRejectingId(null);
      setRejectReason("");
    }
  };

  // ── Loading ──────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-primary-400 animate-spin" />
      </div>
    );
  }

  if (!associationId) {
    return (
      <div className="text-center py-20 text-gray-500">
        No tienes una asociación asignada.
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          Solicitudes de Nuevos Clubes
        </h1>
        <p className="text-gray-500 mt-1">
          Revisa y aprueba las solicitudes de directores que quieren registrar
          un nuevo club.
        </p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Empty state */}
      {requests.length === 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center">
          <Inbox className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">
            No hay solicitudes pendientes
          </h3>
          <p className="text-gray-500">
            Cuando un director registre un nuevo club, aparecerá aquí para tu
            aprobación.
          </p>
        </div>
      )}

      {/* Request Cards */}
      <div className="space-y-4">
        {requests.map((req) => {
          const isProcessing = processingId === req.id;
          const isExpanded = expandedId === req.id;
          const isRejecting = rejectingId === req.id;

          const activeSections = req.clubData?.sections
            ? sortSections(
                Object.entries(req.clubData.sections)
                  .filter(([, v]) => v.active)
                  .map(([k]) => k),
              ).map((k) => CLUB_TYPE_LABELS[k as ClubType] || k)
            : [];

          return (
            <div
              key={req.id}
              className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden"
            >
              {/* Card Header */}
              <div
                className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleExpand(req.id)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <Shield className="w-6 h-6 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 text-lg">
                        {req.clubName}
                      </h3>
                      <p className="text-sm text-gray-500 mt-0.5">
                        Director: {req.userName} • {req.userEmail}
                      </p>
                      {dupWarnings.has(req.id) && (
                        <p className="inline-flex items-center gap-1.5 mt-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-red-50 text-red-700 border border-red-200">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          Posible duplicado: ya existe "
                          {dupWarnings.get(req.id)}" — verifica antes de aprobar
                        </p>
                      )}
                      {activeSections.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {activeSections.map((s) => (
                            <span
                              key={s}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700"
                            >
                              {s}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-xs text-gray-400">
                      {formatDistanceToNow(req.createdAt, {
                        addSuffix: true,
                        locale: es,
                      })}
                    </span>
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="border-t border-gray-100 px-6 pb-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                    {/* Director Info */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                        Datos del Director
                      </h4>
                      <div className="bg-gray-50 rounded-xl p-4 space-y-2.5">
                        <div className="flex items-center gap-2 text-sm">
                          <Users className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-gray-900">
                            {req.personalData.firstName}{" "}
                            {req.personalData.lastName}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-gray-600">{req.userEmail}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-gray-600">
                            {req.personalData.phone}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-gray-600">
                            Nacimiento: {formatDate(req.personalData.birthDate)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-gray-600">
                            {req.personalData.address}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500">
                          Doc: {req.personalData.idDocumentType}{" "}
                          {req.personalData.idDocumentNumber}
                        </div>
                        <div className="text-sm text-gray-500">
                          Género: {req.personalData.gender} • Sangre:{" "}
                          {req.personalData.bloodType}
                        </div>
                      </div>
                    </div>

                    {/* Club Info */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                        Datos del Club
                      </h4>
                      <div className="bg-gray-50 rounded-xl p-4 space-y-2.5">
                        <div className="flex items-center gap-2 text-sm">
                          <Shield className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-gray-900 font-medium">
                            {req.clubName}
                          </span>
                        </div>
                        {req.clubData?.churchName && (
                          <div className="flex items-center gap-2 text-sm">
                            <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <span className="text-gray-600">
                              Iglesia: {req.clubData.churchName}
                            </span>
                          </div>
                        )}
                        {activeSections.length > 0 && (
                          <div className="text-sm">
                            <span className="text-gray-500">Secciones: </span>
                            <span className="text-gray-700">
                              {activeSections.join(", ")}
                            </span>
                          </div>
                        )}
                        <div className="text-sm text-gray-500">
                          Código de invitación:{" "}
                          <code className="bg-gray-200 px-1.5 py-0.5 rounded">
                            {req.clubData?.inviteCode}
                          </code>
                        </div>
                      </div>

                      {/* Emergency Contact */}
                      <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider pt-2">
                        Contacto de Emergencia
                      </h4>
                      <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
                        <p className="text-gray-900">
                          {req.personalData.emergencyContact.name}
                        </p>
                        <p className="text-gray-500">
                          {req.personalData.emergencyContact.relationship} •{" "}
                          {req.personalData.emergencyContact.phone}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Reject Reason Input */}
                  {isRejecting && (
                    <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-4">
                      <label className="block text-sm font-medium text-red-700 mb-2">
                        Motivo del rechazo (requerido)
                      </label>
                      <textarea
                        className="w-full px-3 py-2 border border-red-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        rows={3}
                        placeholder="Explica al director por qué la solicitud no fue aprobada..."
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                      />
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center gap-3 mt-6 pt-4 border-t border-gray-100">
                    {!isRejecting ? (
                      <Can permission="organization.write">
                        <Button
                          onClick={() => handleApprove(req)}
                          disabled={isProcessing}
                          className="flex items-center gap-2"
                        >
                          {isProcessing ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <CheckCircle2 className="w-4 h-4" />
                          )}
                          Aprobar Club
                        </Button>
                        <Button
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setRejectingId(req.id);
                          }}
                          disabled={isProcessing}
                          className="flex items-center gap-2 text-red-600 border-red-300 hover:bg-red-50"
                        >
                          <XCircle className="w-4 h-4" />
                          Rechazar
                        </Button>
                      </Can>
                    ) : (
                      <>
                        <Button
                          variant="outline"
                          onClick={() => handleReject(req)}
                          disabled={isProcessing || !rejectReason.trim()}
                          className="flex items-center gap-2 text-red-600 border-red-300 hover:bg-red-50"
                        >
                          {isProcessing ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <XCircle className="w-4 h-4" />
                          )}
                          Confirmar Rechazo
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => {
                            setRejectingId(null);
                            setRejectReason("");
                          }}
                          disabled={isProcessing}
                        >
                          Cancelar
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
